<template>
  <el-dropdown
    trigger="click"
    data-cy="navbar-main-menu"
    popper-class="navbar-main-menu-popper"
  >
    <!-- Avatar -->
    <NavbarAvatar></NavbarAvatar>

    <template #dropdown>
      <el-dropdown-menu>
        <!-- Avatar with link -->
        <NavbarAvatar :avatarWithLink="true">
          <div
            class="has-text-primary has-cursor"
            @click="toggleModalVisible(ModalStatus.Preferences)"
            data-cy="preferences-button"
          >
            {{ $t("preferences.title") }}
          </div>
        </NavbarAvatar>

        <el-dropdown-item
          v-if="hasCustomerDisplay"
          divided
          @click="
            isCustomerDisplayOpened
              ? closeCustomerDisplay()
              : openCustomerDisplay()
          "
          data-cy="toggle-customer-display-button"
        >
          {{
            isCustomerDisplayOpened
              ? "Close Customer Display"
              : "Open Customer Display"
          }}
        </el-dropdown-item>

        <!-- POS start/end sales -->
        <el-dropdown-item
          v-if="!hasSalesStarted"
          divided
          @click="startSales()"
          data-cy="start-sales-button"
        >
          {{ $t("nav.open-sales") }}
        </el-dropdown-item>

        <!-- POS start/end sales -->
        <el-dropdown-item
          v-if="hasSalesStarted"
          divided
          @click="CashFloatModule.setIsClosingCashfloat(true)"
          data-cy="close-sales-button"
        >
          {{ $t("nav.close-sales") }}
        </el-dropdown-item>

        <!-- Cash in and Cash out -->
        <el-dropdown-item
          data-cy="show-cash-movements-modal"
          v-if="(hasLocation && hasSalesStarted) || isTEST"
          divided
          @click="toggleModalVisible(ModalStatus.CashInOut)"
        >
          Cash In/Out
        </el-dropdown-item>

        <!-- POS settings modal -->
        <el-dropdown-item
          id="show-settings-modal"
          divided
          @click="toggleModalVisible(ModalStatus.Settings)"
          data-cy="pos-settings-button"
        >
          {{ $t("nav.Point-of-sales-settings") }}
        </el-dropdown-item>

        <!-- POS configurations modal -->
        <el-dropdown-item
          divided
          @click="toggleModalVisible(ModalStatus.PosConfig)"
          data-cy="pos-configs-button"
        >
          {{ $t("posconfiguration.title") }}
        </el-dropdown-item>

        <!-- Belgian fiscal reports (X/Z) -->
        <el-dropdown-item
          v-if="isBelgianCompliant"
          divided
          @click="toggleModalVisible(ModalStatus.FiscalReports)"
          data-cy="fiscal-reports-button"
        >
          {{ $t("compliance.fiscal-reports") }}
        </el-dropdown-item>

        <!-- Belgian fiscal archive export -->
        <el-dropdown-item
          v-if="isBelgianCompliant"
          divided
          @click="exportFiscalArchive()"
          data-cy="export-fiscal-archive-button"
        >
          {{ $t("compliance.export-fiscal-archive") }}
        </el-dropdown-item>

        <!-- Backup Data -->
        <el-dropdown-item
          divided
          @click="dataBackupHandler()"
          data-cy="data-backup-button"
        >
          {{ $t("nav.data-backup") }}
        </el-dropdown-item>

        <!-- Sign out -->
        <el-dropdown-item divided @click="onLogOut()" data-cy="sign-out-button">
          <span class="has-text-primary"> {{ $t("nav.sign-out") }} </span>
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref, toRefs } from "vue";

// modules
import AuthModule from "@/store/modules/auth";
import ModalModule from "@/store/modules/modal";

// components
import { NavbarAvatar } from "@/components/index";

// interfaces
import { ModalStatus } from "@/constants/modal";

// helpers
import { redirectToLoginPage } from "@/utils/helpers/router.helper";

//services
import { dexieDB } from "@/services/dexie-database";

import dayjs from "dayjs";
import CashFloatModule from "@/store/modules/cash-float";
import {
  openTopUpForm,
  confirmLogout,
  hasSalesStarted,
} from "@/utils/vue-helpers/cash-float";
import AppModule from "@/store/modules/app";
import DeviceModule from "@/store/modules/device";
import { LogService } from "@/services/logging";
import POSConfigModule from "@/store/modules/pos-config";
import CommonModule from "@/store/modules/common";
import {
  closeCustomerDisplay,
  hasCustomerDisplay,
  openCustomerDisplay,
} from "@/utils/vue-helpers/customer-display";
import { isBelgianCompliant } from "@/utils/vue-helpers/compliance";
import { exportFiscalArchive } from "@/utils/helpers/compliance";

export default defineComponent({
  name: "NavbarMainMenu",
  components: {
    NavbarAvatar,
  },
  setup() {
    const state = reactive({
      onLogOut: async () => {
        // Confirm if the user really doesn't want to close the sales
        // if sales are still open
        if (CashFloatModule.cashFloat) {
          const confirmed = await confirmLogout();
          if (!confirmed) {
            return;
          }
        }

        AuthModule.UserLogOut();
        // remove gId parameter if it is set
        if (AppModule.gid) {
          AppModule.removeGID();
        }
        redirectToLoginPage();
      },
      toggleModalVisible: (item: ModalStatus) => {
        ModalModule.setStatus(item);
      },
      dataBackupHandler: async () => {
        const tables = dexieDB.tables;
        const data: Record<string, any[]> = {};
        for (const table of tables) {
          data[table.name] = await table.toArray();
        }

        //downloading the file
        const a = document.createElement("a");
        const file = new Blob([JSON.stringify(data)], {
          type: "application/json",
        });
        const filename = `smeetz-pos-${dayjs().format(
          "DD-MM-YYYY_HH-mm-ss"
        )}.json`;
        a.href = URL.createObjectURL(file);
        a.download = filename;
        a.click();
      },
      startSales: async () => {
        await openTopUpForm();
      },
    });

    const isCustomerDisplayOpened = computed(() => {
      return CommonModule.isCustomerDisplayOpened;
    });

    // Lets write a computed property that returns true only in TEST environment
    const isTEST = computed(() => {
      const env = import.meta.env.VITE_ENV;
      if (env === "test" || env === "TEST") {
        return true;
      }
      return false;
    });

    const hasLocation = computed(() => {
      // If there is no hardware location (machine not linked to a location or user using a browser) we don't show device settings
      if (DeviceModule.hardwareLocation) {
        return true;
      } else {
        return false;
      }
    });

    return {
      ModalStatus,
      ...toRefs(state),
      hasSalesStarted,
      hasLocation,
      CashFloatModule,
      DeviceModule,
      openCustomerDisplay,
      hasCustomerDisplay,
      closeCustomerDisplay,
      isCustomerDisplayOpened,
      isTEST,
      isBelgianCompliant,
      exportFiscalArchive,
    };
  },
});
</script>
